import React, { useState } from 'react';
import { ScrollArea } from '~/primitives/scroll-area';
import { useSuspenseQuery } from '@tanstack/react-query';
import { levelsQuery } from '~/util/queries/public/levels';
import { Level } from '~/util/types/levels';
import { PlusSquare, X } from 'lucide-react';
import { LevelsSelector } from '~/components/ItemSelector';
import { useList } from '~/util/hooks/useList';

interface PackLevelsSelectorProps {
    levelStates: { [key: string]: boolean };
    setLevelStates: (levelStates: { [key: string]: boolean }) => void;
}

const PackLevelsSelector: React.FC<PackLevelsSelectorProps> = ({
    levelStates,
    setLevelStates,
}) => {
    const list = useList();
    const { data } = useSuspenseQuery(levelsQuery({ list }));

    const [levelSelectorOpen, setLevelSelectorOpen] = useState(false);

    const setItem = (levelid: string, state?: boolean) => {
        const updatedLevelStates = { ...levelStates };

        if (state === undefined) delete updatedLevelStates[levelid];
        else updatedLevelStates[levelid] = state;

        setLevelStates(updatedLevelStates);
    };

    return (
        <div className="bg-background-dark  h-[30vh] rounded-lg p-4 w-full">
            <ScrollArea className="h-full">
                <div className="flex items-center flex-wrap gap-2">
                    {Object.keys(levelStates).map((levelid) => (
                        <button
                            key={levelid}
                            onClick={(event) => {
                                event?.stopPropagation();
                                setItem(levelid, !levelStates[levelid]);
                            }}
                            className={`flex gap-2 items-center px-2 py-1 text-sm font-medium rounded cursor-pointer transition-colors ${
                                levelStates[levelid]
                                    ? 'bg-confirm text-secondary-foreground hover:bg-confirm-transparent'
                                    : 'bg-destructive text-secondary-foreground hover:bg-destructive-transparent'
                            }`}
                        >
                            {
                                data.find(
                                    (level: Level) => level.id === levelid,
                                )?.name
                            }

                            <X
                                className={`h-4 w-4 rounded-full p-px hover:bg-secondary-foreground transition-colors cursor-pointer hover:text-${levelStates[levelid] ? 'confirm' : 'destructive'}`}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setItem(levelid, undefined);
                                }}
                            />
                        </button>
                    ))}
                    <PlusSquare
                        size={24}
                        className="transition-colors text-secondary-light hover:text-secondary-foreground cursor-pointer"
                        onClick={() => setLevelSelectorOpen(true)}
                    />
                    <LevelsSelector
                        initial={[]}
                        onSave={(levels) => {
                            const newLevelStates = { ...levelStates };
                            (levels as Level[]).forEach(
                                (level) => (newLevelStates[level.id] = true),
                            );
                            setLevelStates(newLevelStates);
                        }}
                        open={levelSelectorOpen}
                        onOpenChange={() => setLevelSelectorOpen(false)}
                    />
                </div>
            </ScrollArea>
        </div>
    );
};

export default PackLevelsSelector;
